import { useEffect, useRef } from 'react'
import { getElements } from '@/utils/dom'

type IntersectionObserverInit = {
  root?: Element | Document | null
  rootMargin?: string
  threshold?: number | number[]
}

type UseIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: IntersectionObserverInit,
) => void

const useIntersectionObserver: UseIntersectionObserver = (callback, options = {}) => {
  const observer = useRef<IntersectionObserver | null>(null)

  const disconnect = () => {
    if (!observer.current) {
      throw Error('Not found IntersectionObserver instance')
    }
    return Promise.resolve(observer.current.disconnect())
  }

  const destroy = () => {
    disconnect()
    observer.current = null
  }

  const observerTargeting = () => {
    return getElements('.observed')?.forEach((el) => observer.current?.observe(el))
  }

  const refreshObserver = () => {
    return disconnect().then(observerTargeting)
  }

  const initialize = () => {
    observer.current = new IntersectionObserver(callback, options)

    return observerTargeting()
  }

  useEffect(() => {
    initialize()
    return () => {
      destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    refreshObserver()
  })
}

export default useIntersectionObserver
